import api from '@/lib/axios'
import type { BillingPlan } from './billing'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface BillingPlanFull extends BillingPlan {
  is_active: boolean
  is_custom_pricing: boolean
  stripe_product_id: string | null
  stripe_price_id: string | null
  stripe_annual_price_id: string | null
  max_total_fields: number | null
  max_rows_per_document: number | null
  chat_retrieval_limit: number
  chat_context_token_budget: number
  chat_min_similarity: number
  default_extraction_prompt: string | null
  limit_overrides: Record<string, unknown>
  model_count: number
  whitelabel_enabled: boolean
}

export interface BillingPlanCreateData {
  name: string
  slug: string
  price_cents?: number
  billing_interval?: string
  description?: string | null
  is_hidden?: boolean
  is_active?: boolean
  is_custom_pricing?: boolean
  max_projects?: number | null
  max_documents_per_project?: number | null
  max_fields_per_project?: number | null
  max_processed_documents?: number | null
  max_total_fields?: number | null
  max_rows_per_document?: number | null
  max_users?: number | null
  max_storage_mb?: number | null
  chat_enabled?: boolean
  whitelabel_enabled?: boolean
  chat_retrieval_limit?: number
  chat_context_token_budget?: number
  chat_min_similarity?: number
  credits_per_page_batch_mode?: number
  chat_credits_per_message?: number
  credit_discount_factor?: number
  monthly_wallet_credits?: number
  annual_price_cents?: number | null
  annual_free_months?: number
  annual_discount_percent?: number
  default_extraction_prompt?: string | null
  limit_overrides?: Record<string, unknown>
}

export interface PlanLlmModel {
  id: string
  plan_id: string
  model_name: string
  display_name: string
  is_default: boolean
  credits_per_page_batch_mode: number
  chat_credits_per_message: number
  sort_order: number
  is_active: boolean
}

export interface PlanLlmModelCreateData {
  model_name: string
  display_name: string
  is_default?: boolean
  credits_per_page_batch_mode: number
  chat_credits_per_message: number
  sort_order?: number
  is_active?: boolean
}

export interface PlanLlmModelUpdateData {
  display_name?: string
  is_default?: boolean
  credits_per_page_batch_mode?: number
  chat_credits_per_message?: number
  sort_order?: number
  is_active?: boolean
}

export interface DiscoveredModel {
  provider: string
  model_id: string
  display_name: string
}

export interface OrgSubscriptionData {
  id: string | null
  org_id: string
  plan_id: string | null
  plan_name: string | null
  plan_slug: string | null
  status: string | null
  stripe_subscription_id: string | null
  current_period_start: string | null
  current_period_end: string | null
  custom_pricing: Record<string, unknown> | null
  created_at: string | null
  updated_at: string | null
}

export interface WalletData {
  // Credits-denominated since migration 015.
  balance_credits: number
  transactions: Array<{
    id: string
    type: string
    amount_credits: number
    balance_after_credits: number
    description: string | null
    created_at: string | null
  }>
}

export interface MonthlyUsage {
  month: string
  documents_processed: number
  pages_processed: number
  extraction_runs: number
  tokens_used: number
  credits_consumed: number
  credits_added: number
  storage_used_mb: number
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export const adminApi = {
  // Users
  listUsers: () => api.get('/admin/users').then((r) => r.data),
  getUser: (id: string) => api.get(`/admin/users/${id}`).then((r) => r.data),
  updateUser: (id: string, data: Record<string, unknown>) =>
    api.patch(`/admin/users/${id}`, data).then((r) => r.data),
  deactivateUser: (id: string) => api.delete(`/admin/users/${id}`).then((r) => r.data),

  // Orgs
  listOrgs: () => api.get('/admin/orgs').then((r) => r.data),
  getOrg: (id: string) => api.get(`/admin/orgs/${id}`).then((r) => r.data),
  updateOrg: (id: string, data: Record<string, unknown>) =>
    api.patch(`/admin/orgs/${id}`, data).then((r) => r.data),

  // Org Subscription
  getOrgSubscription: (orgId: string) =>
    api.get<OrgSubscriptionData>(`/admin/orgs/${orgId}/subscription`).then((r) => r.data),
  assignOrgSubscription: (orgId: string, data: { plan_id: string; status: string; current_period_start?: string; current_period_end?: string }) =>
    api.post(`/admin/orgs/${orgId}/subscription`, data).then((r) => r.data),
  updateOrgSubscription: (orgId: string, data: { plan_id?: string; status?: string; current_period_start?: string; current_period_end?: string }) =>
    api.patch(`/admin/orgs/${orgId}/subscription`, data).then((r) => r.data),

  // Org Wallet
  getOrgWallet: (orgId: string) =>
    api.get<WalletData>(`/admin/orgs/${orgId}/wallet`).then((r) => r.data),
  adjustWallet: (orgId: string, data: { amount_credits: number; type: string; description: string }) =>
    api.post(`/admin/orgs/${orgId}/wallet/adjust`, data).then((r) => r.data),

  // Org Usage
  getOrgUsage: (orgId: string, months?: number) =>
    api.get<MonthlyUsage[]>(`/admin/orgs/${orgId}/usage`, { params: { months: months ?? 6 } }).then((r) => r.data),

  // Plans
  listPlans: () => api.get<BillingPlanFull[]>('/admin/plans').then((r) => r.data),
  getPlan: (id: string) => api.get<BillingPlanFull>(`/admin/plans/${id}`).then((r) => r.data),
  createPlan: (data: BillingPlanCreateData) =>
    api.post<BillingPlanFull>('/admin/plans', data).then((r) => r.data),
  updatePlan: (id: string, data: Partial<BillingPlanCreateData>) =>
    api.patch<BillingPlanFull>(`/admin/plans/${id}`, data).then((r) => r.data),
  deletePlan: (id: string) => api.delete(`/admin/plans/${id}`).then((r) => r.data),

  // Plan LLM Models
  listPlanModels: (planId: string) =>
    api.get<PlanLlmModel[]>(`/admin/plans/${planId}/models`).then((r) => r.data),
  addPlanModel: (planId: string, data: PlanLlmModelCreateData) =>
    api.post<PlanLlmModel>(`/admin/plans/${planId}/models`, data).then((r) => r.data),
  updatePlanModel: (planId: string, modelId: string, data: PlanLlmModelUpdateData) =>
    api.patch<PlanLlmModel>(`/admin/plans/${planId}/models/${modelId}`, data).then((r) => r.data),
  removePlanModel: (planId: string, modelId: string) =>
    api.delete(`/admin/plans/${planId}/models/${modelId}`).then((r) => r.data),

  // Plan actions
  duplicatePlan: (planId: string) =>
    api.post<BillingPlanFull>(`/admin/plans/${planId}/duplicate`).then((r) => r.data),
  syncPlanStripe: (planId: string) =>
    api.post(`/admin/plans/${planId}/sync-stripe`).then((r) => r.data),

  // Model Discovery
  discoverModels: () =>
    api.get<DiscoveredModel[]>('/admin/models/discover').then((r) => r.data),

  // Settings
  listSettings: () => api.get('/admin/settings').then((r) => r.data),
  updateSetting: (key: string, value: string) =>
    api.put(`/admin/settings/${key}`, { value }).then((r) => r.data),

  // Impersonation
  impersonate: (userId: string) =>
    api.post(`/admin/impersonate/${userId}`).then((r) => r.data),
}
